import camelCase from 'lodash/camelCase';
import mapKeys from 'lodash/mapKeys';
import {
    FintechDiscountItemDto,
    FintechDiscountItemModel,
    PostDto,
    PostModel,
    PostStatDto,
    PostStatItemDto,
    PostStatModel,
    ProductItemDto,
    ProductItemModel,
} from '../../data-layer/posts/types';
import { fromBusinessLiteDto } from '../business/toBusinessModel';
import { toPostContentModel } from './toPostContentModel';

const toFintechDiscountItem = (
    item?: FintechDiscountItemDto,
): FintechDiscountItemModel | undefined => {
    if (!item) {
        return undefined;
    }

    return {
        discountPrice: item.discount_price,
        discountPercent: item.discount_percent,
        maxDiscountAmount: item.max_discount_amount,
        maxDiscountPercent: item.max_discount_percent,
    };
};

export const toProductModel = (product: ProductItemDto): ProductItemModel => ({
    id: product.id,
    url: product.url,
    originalUrl: product.original_url,
    title: product.title,
    content: toPostContentModel(product.content),
    priceInfo: {
        currency: product.price_info.currency,
        integerPart: product.price_info.integer_part,
        fractionalPart: product.price_info.fractional_part,
        discountPercent: product.price_info.discount_percent,
    },
    oldPriceInfo: {
        currency: product.old_price_info?.currency ?? '',
        integerPart: product.old_price_info?.integer_part ?? 0,
        fractionalPart: product.old_price_info?.fractional_part ?? 0,
        discountPercent: product.old_price_info?.discount_percent,
    },
    monetizationEnabled: product.monetization_enabled,
    isUnavailable: product.is_unavailable,
    fintechDiscount: product.fintech_discount
        ? {
              yaPay: toFintechDiscountItem(product.fintech_discount.ya_pay),
              yaSplit: toFintechDiscountItem(product.fintech_discount.ya_split),
          }
        : undefined,
    personalDiscount: product.personal_discount
        ? { type: product.personal_discount.type }
        : undefined,
    checkout: product.checkout
        ? {
              url: product.checkout?.url,
          }
        : undefined,
});

export const toPostModel = (postDto: PostDto): PostModel => ({
    id: postDto.id,
    text: postDto.text,
    reactionState: postDto.reaction_state,
    saved: postDto.saved,
    likes: postDto.public_stats?.likes ?? 0,
    views: postDto.views ?? 0,
    clickouts: postDto.clickouts?.map(
        (clickout) => mapKeys(clickout, (_, key) => camelCase(key)) as any,
    ),
    tags: postDto.tags,
    status: postDto.status,
    declinedReason: postDto.declined_reason,
    source: postDto.data_source,
    url:
        postDto.post_type === 'collection'
            ? `/collections/${postDto.id}`
            : `/businesses/${postDto.business ? postDto.business.public_id : 'post'}/posts/${
                  postDto.id
              }`,
    shareUrl: postDto.post_url,
    business: postDto.business ? fromBusinessLiteDto(postDto.business) : undefined,
    createdAt: postDto.created_at,
    djRequestId: postDto.dj_request_id,
    logContext: postDto.log_context,
    content: toPostContentModel(postDto.content),
    adsData: postDto.ads_data
        ? {
              showUrl: postDto.ads_data?.show_url,
          }
        : undefined,
    products: {
        title: postDto.products_info?.title ?? '',
        description: postDto.products_info?.description ?? '',
        buttonTitle: postDto.products_info?.button_title ?? '',
        items: postDto.products_info ? postDto.products_info?.items?.map(toProductModel) : [],
        businessButtonTitle: postDto.products_info?.business_button_title ?? '',
    },
    postType: postDto.post_type,
    ownersData: postDto.owners_data
        ? {
              author: {
                  id: postDto.owners_data?.author.id,
                  displayName: postDto.owners_data?.author.display_name,
                  avatarUrl: postDto.owners_data?.author.avatar_url,
                  shareLink: postDto.owners_data?.author.share_link,
              },
              shop: {
                  displayUrl: postDto.owners_data?.shop?.display_url,
              },
              isOwner: postDto.owners_data?.is_owner,
          }
        : undefined,
    collectionData: postDto.collection_data
        ? {
              id: postDto.collection_data.id,
              title: postDto.collection_data.title,
              numberOfPosts: postDto.collection_data.number_of_posts,
              buttonText: postDto.collection_data.button_text,
              isSaved: postDto.collection_data.is_saved,
          }
        : undefined,
    // Если информация о бусте отсутствует, то по умолчанию считаем его включённым.
    enabledDefaultBoosting: postDto.enabled_default_boosting ?? true,
    stats: postDto.stats ? toPostStatsModel(postDto.stats) : undefined,
    publicStats: postDto.public_stats,
    monetizationEnabled: postDto.monetization_enabled,
    isAIContent: postDto.is_ai_content,
});

const getStatItem = (businessStatItemDto: PostStatItemDto) => ({
    value: businessStatItemDto.value,
    display: businessStatItemDto.display,
    diffValue: businessStatItemDto.diff_value,
    diffDisplay: businessStatItemDto.diff_display,
});

export const toPostStatsModel = (statsDto: PostStatDto): PostStatModel => ({
    coverage: {
        total: getStatItem(statsDto.coverage.total),
        subscribers: getStatItem(statsDto.coverage.subscribers),
        notsubscribers: getStatItem(statsDto.coverage.notsubscribers),
        boosted: statsDto.coverage.boosted && getStatItem(statsDto.coverage.boosted),
        organic: statsDto.coverage.organic && getStatItem(statsDto.coverage.organic),
    },
    involved: {
        total: getStatItem(statsDto.involved.total),
        subscribers: getStatItem(statsDto.involved.subscribers),
        notsubscribers: getStatItem(statsDto.involved.notsubscribers),
        boosted: statsDto.involved.boosted && getStatItem(statsDto.involved.boosted),
        organic: statsDto.involved.organic && getStatItem(statsDto.involved.organic),
    },
    shows: {
        total: getStatItem(statsDto.shows.total),
        subscribers: getStatItem(statsDto.shows.subscribers),
        notsubscribers: getStatItem(statsDto.shows.notsubscribers),
        boosted: statsDto.shows.boosted && getStatItem(statsDto.shows.boosted),
        organic: statsDto.shows.organic && getStatItem(statsDto.shows.organic),
    },
    showsInGrid: {
        total: getStatItem(statsDto.shows_in_grid.total),
        subscribers: getStatItem(statsDto.shows_in_grid.subscribers),
        notsubscribers: getStatItem(statsDto.shows_in_grid.notsubscribers),
        boosted: statsDto.shows_in_grid.boosted && getStatItem(statsDto.shows_in_grid.boosted),
        organic: statsDto.shows_in_grid.organic && getStatItem(statsDto.shows_in_grid.organic),
    },
    subscribers: {
        total: getStatItem(statsDto.subscribers.total),
        subscribed: getStatItem(statsDto.subscribers.subscribed),
        unsubscribed: getStatItem(statsDto.subscribers.unsubscribed),
    },
    interactions: {
        total: getStatItem(statsDto.interactions.total),
        clicks: getStatItem(statsDto.interactions.clicks),
        likes: getStatItem(statsDto.interactions.likes),
        shares: getStatItem(statsDto.interactions.shares),
        saves: getStatItem(statsDto.interactions.saves),
    },
});
